import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView } from "@/utils/analytics-tracking";

interface UsePageTrackingOptions {
  /**
   * false로 설정하면 트래킹이 비활성화된다.
   * Demo 모드나 미인증 상태에서 사용.
   * 기본값: true
   */
  enabled?: boolean;
}

/**
 * React Router 6의 location 변경을 감지하여 페이지 뷰를 Firebase Analytics에 기록한다.
 *
 * - PII 마스킹은 trackPageView 내부에서 처리된다.
 * - React Strict Mode의 이중 실행으로 인한 중복 트래킹을 ref로 방지한다.
 * - enabled=false 설정으로 Demo 모드 등에서 트래킹을 비활성화할 수 있다.
 *
 * @example
 * // App.tsx (instructor-web)
 * function App() {
 *   const { isDemoMode } = useAuth();
 *   usePageTracking({ enabled: !isDemoMode() });
 *   return <RouterOutlet />;
 * }
 */
export function usePageTracking({ enabled = true }: UsePageTrackingOptions = {}): void {
  const { pathname } = useLocation();
  const lastTrackedPath = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      // 비활성화 시 ref를 초기화하여, 재활성화되면 현재 경로를 다시 트래킹하도록 한다.
      lastTrackedPath.current = null;
      return;
    }

    // 동일 경로 중복 트래킹 방지 (React Strict Mode 이중 실행 포함)
    if (lastTrackedPath.current === pathname) return;

    lastTrackedPath.current = pathname;
    trackPageView(pathname);
  }, [pathname, enabled]);
}
